/**
 * 24-hour timeline view for relay schedules on the site dashboard.
 *
 * Renders every active relay schedule as a horizontal bar spanning a
 * 24-hour track, giving operators an at-a-glance view of when circuits
 * are energised — the dashboard's hardware-truth counterpart to the
 * calendar view's event-series intent. Sun-relative schedules
 * (sunset/sunrise ± offset) are resolved to a fixed minute position using
 * the site's next_sunset / next_sunrise values. Overnight windows (off
 * time < on time) are drawn as two segments that wrap around midnight.
 * The on/off minute math itself lives in scheduleTimelineSpans.js, shared
 * with the calendar-mode "next change" label.
 *
 * Contains:
 * - ScheduleTimeline — the rendered timeline component
 */
import { Box, Typography } from '@mui/material';
import { t } from 'i18next';
import React from 'react';

import { scheduleEnd, scheduleStart } from '@sophocles/entities/RelaySchedule';
import { buildDayPillBooleans, DayPills } from '@sophocles/views/sites/components/RelayCards/dayPills';
import { resolveActiveScheduleSpans } from '@sophocles/views/sites/components/RelayCards/scheduleTimelineSpans';

// Hour marks displayed as gridlines along the 24 h track.
const HOUR_TICKS = [0, 6, 12, 18, 24];

/**
 * Converts a minute value (0–1440) to a percentage string for CSS positioning.
 *
 * @param {number} minutes
 * @returns {string}
 */
function pct(minutes) {
  return `${((minutes / 1440) * 100).toFixed(3)}%`;
}

/**
 * A single coloured segment on the 24 h bar.
 *
 * @param {{ fromMinutes: number, toMinutes: number }} props
 */
function Segment({ fromMinutes, toMinutes }) {
  const left = pct(fromMinutes);
  const width = pct(toMinutes - fromMinutes);
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left,
        width,
        bgcolor: 'primary.main',
        borderRadius: 'var(--radius-sm)',
      }}
    />
  );
}

/**
 * 24-hour timeline for one relay's active schedules. Accepts the relay's
 * full schedule list — disabled rows are filtered out internally.
 *
 * @param {{
 *   schedules: object[],
 *   user: object,
 *   dimmerAvailable: boolean,
 *   onClickEditSchedule: (id: number) => void,
 *   nextSunrise: Date|null,
 *   nextSunset: Date|null,
 *   dayOfWeekScheduling: boolean,
 * }} props
 */
function ScheduleTimeline({
  schedules,
  user,
  dimmerAvailable,
  onClickEditSchedule,
  nextSunrise,
  nextSunset,
  dayOfWeekScheduling,
}) {
  const scheduleById = new Map(schedules.map(schedule => [schedule.id, schedule]));
  const scheduleSpans = resolveActiveScheduleSpans(schedules, nextSunrise, nextSunset);

  return (
    <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
      {/* Hour tick labels */}
      <Box sx={{ position: 'relative', height: 14, mb: 0.25 }}>
        {HOUR_TICKS.map(hour => (
          <Typography
            key={hour}
            variant="caption"
            sx={{
              position: 'absolute',
              left: pct(hour * 60),
              transform: hour === 24 ? 'translateX(-100%)' : hour === 0 ? 'none' : 'translateX(-50%)',
              color: 'text.disabled',
              fontSize: '0.6rem',
              lineHeight: 1,
              userSelect: 'none',
            }}
          >
            {hour === 24 ? '24' : `${hour}`}
          </Typography>
        ))}
      </Box>

      {/* One row per active schedule */}
      {scheduleSpans.map(span => {
        const schedule = scheduleById.get(span.scheduleId);
        const daysOfWeek = schedule.days_of_week ?? null;
        const dutyCycle = schedule.duty_cycle ?? 100;

        // Always build pills for DOW-capable units; pass 0 for unset daysOfWeek
        // so inactive days show greyed rather than calling buildDayPillBooleans(null)
        // which would treat null as ALL_DAYS_MASK (all-active).
        const dayPillBooleans = dayOfWeekScheduling ? buildDayPillBooleans(daysOfWeek ?? 0) : null;

        // Resolve human-readable edge labels for on/off
        const onLabel = scheduleStart(
          span.alwaysOn,
          schedule.on_time ?? null,
          schedule.on_type ?? null,
          schedule.on_offset ?? null,
          nextSunrise,
          nextSunset,
          user,
          t,
        );
        const offLabel = scheduleEnd(
          span.alwaysOn,
          schedule.off_time ?? null,
          schedule.off_type ?? null,
          schedule.off_offset ?? null,
          nextSunrise,
          nextSunset,
          user,
          t,
        );

        return (
          <Box
            key={span.scheduleId}
            onClick={() => onClickEditSchedule(span.scheduleId)}
            sx={{ mb: 1.5, cursor: 'pointer' }}
          >
            {/* On→off label row */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 18 }}>
                {span.scheduleNumber}.
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {span.alwaysOn ? t('sites.alwaysOn') : `${onLabel} → ${offLabel}`}
              </Typography>
            </Box>

            {/* 24 h bar */}
            <Box
              sx={{
                position: 'relative',
                height: 10,
                borderRadius: 'var(--radius-sm)',
                bgcolor: 'action.hover',
                overflow: 'hidden',
              }}
            >
              {/* Hour gridlines */}
              {HOUR_TICKS.filter(h => h > 0 && h < 24).map(hour => (
                <Box
                  key={hour}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: pct(hour * 60),
                    width: '1px',
                    bgcolor: 'divider',
                    zIndex: 0,
                  }}
                />
              ))}

              {/* Active window segment(s) */}
              {span.segments.map(segment => (
                <Segment key={segment.fromMinutes} fromMinutes={segment.fromMinutes} toMinutes={segment.toMinutes} />
              ))}
            </Box>

            {/* Day pills for day-of-week schedules */}
            {dayPillBooleans ? <DayPills days={dayPillBooleans} /> : null}

            {/* Dimming label when available */}
            {dimmerAvailable ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                {t('sites.brightnessPercent', 'Brightness (%): ') + dutyCycle}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

export default ScheduleTimeline;
